import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  inject,
  input,
  Input,
  Output,
} from '@angular/core';
import { NgForOf, NgIf, NgStyle, NgTemplateOutlet } from '@angular/common';
import { Router } from '@angular/router';

import { ImageLoadingComponent } from '../image-loading/image-loading.component';
import { Urls } from '@helper/urls';
import { WindowDetectService } from '@service/window-detect.service';
import { Links } from '@helper/links';
import { WaterfallItem } from '@common/waterfall/model';
import { LinkToUrl } from '@common/link-pipes';
import { UniversalLinkComponent } from '@common/universal-link/universal-link.component';
import { FocusRemoverDirective } from '@helper/focus-remover.directive';

@Component({
  selector: 'app-waterfall',
  templateUrl: './waterfall.component.html',
  styleUrls: ['./waterfall.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ImageLoadingComponent,
    NgForOf,
    NgIf,
    NgStyle,
    NgTemplateOutlet,
    LinkToUrl,
    UniversalLinkComponent,
    FocusRemoverDirective,
  ],
})
export class WaterfallComponent {
  // if width does not set, it will fill the whole available spaces
  readonly width = input(0);
  readonly gapSize = input(0);
  readonly columns = input<WaterfallItem[][]>([]);
  readonly imgWidth = computed(
    () =>
      this.width() ||
      Math.floor(
        (this.windowService.windowWidth() - (this.columns().length - 1) * this.gapSize()) / this.columns().length
      )
  );
  readonly imgWidthCss = computed(() =>
    this.windowService.isMobileDevice()
      ? `calc((100vw - ${(this.columns().length - 1) * this.gapSize()}px)/${this.columns().length})`
      : `${this.imgWidth()}px`
  );

  private readonly router = inject(Router);
  private readonly windowService = inject(WindowDetectService);

  @Output() readonly itemClick = new EventEmitter<WaterfallItem>();

  @Input() showTitle = false;
  @Input() itemStyle: { [key: string]: string } | undefined;
  @Input() headerHeight = 0;
  @Input() hasQuickLink = false;

  onItemClick(waterfall: WaterfallItem, event: MouseEvent | undefined = undefined): void {
    this.itemClick.emit(waterfall);
    if (!waterfall?.item || !event) {
      return;
    }
    let link = waterfall.item.link;
    let meta = waterfall.item.meta;
    if (waterfall.item.segmented) {
      const target: HTMLElement = event.target as HTMLElement;
      if (!target) {
        return;
      }
      const localX = event.pageX - target.offsetLeft;
      const localY = event.pageY - target.offsetTop - this.headerHeight;
      const segmented = waterfall.item.segmented.links.find(
        l =>
          l.tlPoint &&
          l.brPoint &&
          (l.tlPoint.pX * target.clientWidth) / 100 <= localX &&
          (l.tlPoint.pY * target.clientHeight) / 100 <= localY &&
          (l.brPoint.pX * target.clientWidth) / 100 >= localX &&
          (l.brPoint.pY * target.clientHeight) / 100 >= localY
      );
      if (segmented && (segmented.link || segmented.meta)) {
        link = segmented.link;
        meta = segmented.meta;
      }
    }
    if (meta) {
      Links.handleLink(this.router, Urls.fromMeta(meta));
    } else if (link) {
      Links.handleLink(this.router, link);
    }
  }

  trackByFn(index: number, item: WaterfallItem): string {
    return item.item?.image?.hash || index.toString();
  }
}
